import { useEffect, useState } from "react";
import { DownloadSimple, Printer } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatErr, todayLocal } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTranslation } from "react-i18next";

const STATUS_META = {
  present: { labelKey: "present", label: "Present", cls: "bg-status-green/10 text-status-green" },
  absent: { labelKey: "absent", label: "Absent", cls: "bg-status-red/10 text-status-red" },
  leave: { labelKey: "approvedLeave", label: "Approved Leave", cls: "bg-status-blue/10 text-status-blue" },
  unmarked: { labelKey: "notMarked", label: "Not Marked", cls: "bg-slate-100 text-slate-400" },
};

const CELL_TINT = { P: "text-status-green font-bold", A: "text-status-red font-bold", L: "text-status-blue font-bold", "—": "text-slate-300" };

const threeYearsAgo = () => {
  const t = todayLocal();
  return `${parseInt(t.slice(0, 4), 10) - 3}${t.slice(4)}`;
};

async function fetchPdf(path, params, filename, openInTab) {
  try {
    const res = await api.get(path, { params, responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    if (openInTab) {
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } else {
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 8000);
    }
  } catch (e) {
    toast.error(formatErr(e, "PDF export failed"));
  }
}

const PdfButtons = ({ path, params, filename, disabled, testidPrefix }) => {
  const { t } = useTranslation();
  return (
  <div className="flex gap-2 ml-auto">
    <Button variant="outline" data-testid={`${testidPrefix}-print-btn`} onClick={() => fetchPdf(path, params, filename, true)} disabled={disabled} className="rounded-full font-bold border-slate-300 transition-transform duration-300 active:scale-95">
      <Printer size={15} className="mr-1.5" /> {t("teacherAttendanceReports.print", { defaultValue: "Print" })}
    </Button>
    <Button variant="outline" data-testid={`${testidPrefix}-pdf-btn`} onClick={() => fetchPdf(path, params, filename, false)} disabled={disabled} className="rounded-full font-bold border-slate-300 transition-transform duration-300 active:scale-95">
      <DownloadSimple size={15} className="mr-1.5" /> {t("teacherAttendanceReports.downloadPdf", { defaultValue: "Download PDF" })}
    </Button>
  </div>
  );
};

export function DailyReport({ apiBase, classes, showSummary = false }) {
  const { t } = useTranslation();
  const [date, setDate] = useState(todayLocal());
  const [classId, setClassId] = useState("");
  const [data, setData] = useState(null);
  const [summaryRows, setSummaryRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!classId && classes.length) setClassId(classes[0].id);
  }, [classes, classId]);

  useEffect(() => {
    if (!classId) return;
    const run = async () => {
      setLoading(true);
      try {
        const res = await api.get(`${apiBase}/daily-report`, { params: { date, class_id: classId } });
        setData(res.data);
        if (showSummary) {
          const sum = await api.get(`${apiBase}/daily-summary`, { params: { date } });
          setSummaryRows(sum.data.rows);
        }
      } catch (e) {
        toast.error(formatErr(e));
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [date, classId]); // eslint-disable-line react-hooks/exhaustive-deps

  const s = data?.summary;
  return (
    <div data-testid="daily-report">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Input type="date" data-testid="daily-report-date" value={date} min={threeYearsAgo()} max={todayLocal()} onChange={(e) => setDate(e.target.value)} className="w-40 bg-white" />
        <Select value={classId} onValueChange={setClassId}>
          <SelectTrigger data-testid="daily-report-class" className="w-44 bg-white"><SelectValue placeholder={t("teacherAttendanceReports.selectClass", { defaultValue: "Select class" })} /></SelectTrigger>
          <SelectContent>
            {classes.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <PdfButtons path={`${apiBase}/daily-report/pdf`} params={{ date, class_id: classId }} filename={`attendance-daily-${date}.pdf`} disabled={!data} testidPrefix="daily-report" />
      </div>

      {showSummary && summaryRows.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden overflow-x-auto mb-4" data-testid="daily-all-classes-summary">
          <table className="w-full text-sm min-w-[560px]">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                {[t("teacherAttendanceReports.class", { defaultValue: "Class" }), t("teacherAttendanceReports.students", { defaultValue: "Students" }), t("teacherAttendanceReports.present", { defaultValue: "Present" }), t("teacherAttendanceReports.absent", { defaultValue: "Absent" }), t("teacherAttendanceReports.leave", { defaultValue: "Leave" }), t("teacherAttendanceReports.notMarked", { defaultValue: "Not Marked" }), "%"].map((h) => <th key={h} className="px-4 py-2.5">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {summaryRows.map((r) => (
                <tr key={r.class_id} data-testid={`daily-summary-row-${r.class_id}`} onClick={() => setClassId(r.class_id)} className={`border-t border-slate-100 cursor-pointer transition-colors duration-150 ${r.class_id === classId ? "bg-status-blue/5" : "hover:bg-slate-50"}`}>
                  <td className="px-4 py-2.5 font-semibold">{r.class_name}</td>
                  <td className="px-4 py-2.5">{r.total}</td>
                  <td className="px-4 py-2.5 text-status-green font-semibold">{r.present}</td>
                  <td className="px-4 py-2.5 text-status-red font-semibold">{r.absent}</td>
                  <td className="px-4 py-2.5 text-status-blue font-semibold">{r.leave}</td>
                  <td className="px-4 py-2.5 text-slate-400">{r.not_marked}</td>
                  <td className="px-4 py-2.5 font-bold">{r.pct === null ? "—" : `${r.pct}%`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {loading && <p className="text-sm text-slate-400">{t("teacherAttendanceReports.loading", { defaultValue: "Loading…" })}</p>}
      {data && !loading && s && (
        <>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2.5 mb-4" data-testid="daily-summary-cards">
            {[[t("teacherAttendanceReports.totalStudents", { defaultValue: "Total Students" }), s.total, "text-slate-700"], [t("teacherAttendanceReports.present", { defaultValue: "Present" }), s.present, "text-status-green"], [t("teacherAttendanceReports.absent", { defaultValue: "Absent" }), s.absent, "text-status-red"], [t("teacherAttendanceReports.approvedLeave", { defaultValue: "Approved Leave" }), s.leave, "text-status-blue"], [t("teacherAttendanceReports.notMarked", { defaultValue: "Not Marked" }), s.not_marked, "text-slate-400"], [t("teacherAttendanceReports.attendance", { defaultValue: "Attendance" }), s.pct === null ? "—" : `${s.pct}%`, "text-slate-700"]].map(([label, val, tint]) => (
              <div key={label} className="bg-white border border-slate-200 rounded-xl p-3 text-center">
                <p className={`font-heading text-xl font-extrabold ${tint}`}>{val}</p>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mt-0.5">{label}</p>
              </div>
            ))}
          </div>
          {s.not_marked === s.total ? (
            <p className="bg-white border border-slate-200 rounded-2xl p-8 text-center text-sm text-slate-400" data-testid="daily-empty-state">{t("teacherAttendanceReports.noRecords", { defaultValue: "No attendance records available for the selected period." })}</p>
          ) : (
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden overflow-x-auto">
              <table className="w-full text-sm min-w-[420px]">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                    <th className="px-5 py-3">{t("teacherAttendanceReports.rollNo", { defaultValue: "Roll No." })}</th>
                    <th className="px-5 py-3">{t("teacherAttendanceReports.studentName", { defaultValue: "Student Name" })}</th>
                    <th className="px-5 py-3">{t("teacherAttendanceReports.status", { defaultValue: "Status" })}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.student_id} className="border-t border-slate-100" data-testid={`daily-row-${r.student_id}`}>
                      <td className="px-5 py-3 text-slate-500">{r.roll_no || "—"}</td>
                      <td className="px-5 py-3 font-semibold">{r.name}</td>
                      <td className="px-5 py-3">
                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${STATUS_META[r.status].cls}`}>{t(`teacherAttendanceReports.${STATUS_META[r.status].labelKey}`, { defaultValue: STATUS_META[r.status].label })}</span>
                        {r.leave_reason && <span className="text-xs text-slate-400 ml-2">{r.leave_reason}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function MonthlyReport({ apiBase, classes }) {
  const { t } = useTranslation();
  const [month, setMonth] = useState(todayLocal().slice(0, 7));
  const [classId, setClassId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!classId && classes.length) setClassId(classes[0].id);
  }, [classes, classId]);

  useEffect(() => {
    if (!classId || !month) return;
    const run = async () => {
      setLoading(true);
      try {
        const res = await api.get(`${apiBase}/monthly-report`, { params: { month, class_id: classId } });
        setData(res.data);
      } catch (e) {
        toast.error(formatErr(e));
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [month, classId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="monthly-report">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Input type="month" data-testid="monthly-report-month" value={month} min={threeYearsAgo().slice(0, 7)} max={todayLocal().slice(0, 7)} onChange={(e) => setMonth(e.target.value)} className="w-44 bg-white" />
        <Select value={classId} onValueChange={setClassId}>
          <SelectTrigger data-testid="monthly-report-class" className="w-44 bg-white"><SelectValue placeholder={t("teacherAttendanceReports.selectClass", { defaultValue: "Select class" })} /></SelectTrigger>
          <SelectContent>
            {classes.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <PdfButtons path={`${apiBase}/monthly-report/pdf`} params={{ month, class_id: classId }} filename={`attendance-monthly-${month}.pdf`} disabled={!data} testidPrefix="monthly-report" />
      </div>
      {loading && <p className="text-sm text-slate-400">{t("teacherAttendanceReports.loading", { defaultValue: "Loading…" })}</p>}
      {data && !loading && !data.has_records && (
        <p className="bg-white border border-slate-200 rounded-2xl p-8 text-center text-sm text-slate-400" data-testid="monthly-empty-state">{t("teacherAttendanceReports.noRecords", { defaultValue: "No attendance records available for the selected period." })}</p>
      )}
      {data && !loading && data.has_records && (
        <>
          <p className="text-xs text-slate-400 mb-2">
            {t("teacherAttendanceReports.monthlyLegend", { defaultValue: "P = Present · A = Absent · L = Approved Leave · — = Not Marked · WD = days with attendance recorded for the class · % = Present ÷ WD × 100" })}
          </p>
          <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto" data-testid="monthly-register">
            <table className="text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <th className="px-3 py-2.5 sticky left-0 bg-white z-10">{t("teacherAttendanceReports.roll", { defaultValue: "Roll" })}</th>
                  <th className="px-3 py-2.5 sticky left-12 bg-white z-10">{t("teacherAttendanceReports.student", { defaultValue: "Student" })}</th>
                  {data.dates.map((d) => (
                    <th key={d} className="px-1 py-2.5 text-center min-w-6">{parseInt(d.slice(8), 10)}</th>
                  ))}
                  {["P", "A", "L", "WD", "%"].map((h) => <th key={h} className="px-2 py-2.5 text-center font-bold text-slate-500">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.student_id} className="border-t border-slate-100" data-testid={`monthly-row-${r.student_id}`}>
                    <td className="px-3 py-2 sticky left-0 bg-white text-slate-500">{r.roll_no || "—"}</td>
                    <td className="px-3 py-2 sticky left-12 bg-white font-semibold whitespace-nowrap">{r.name}</td>
                    {data.dates.map((d) => (
                      <td key={d} className={`px-1 py-2 text-center ${CELL_TINT[r.cells[d]]}`}>{r.cells[d]}</td>
                    ))}
                    <td className="px-2 py-2 text-center font-bold text-status-green">{r.present}</td>
                    <td className="px-2 py-2 text-center font-bold text-status-red">{r.absent}</td>
                    <td className="px-2 py-2 text-center font-bold text-status-blue">{r.leave}</td>
                    <td className="px-2 py-2 text-center text-slate-500">{r.working_days}</td>
                    <td className="px-2 py-2 text-center font-extrabold">{r.pct === null ? "—" : `${r.pct}%`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
